import type { ReactNode } from 'react';
import { Loader2, ScanLine, Square } from 'lucide-react';
import { ScanControls } from './ScanControls';
import { ScanKeptOffer } from './ScanKeptOffer';
import { ScanMore, ScanSavedNote } from './ScanMore';
import { ScanPages } from './ScanPages';
import { ScanProblemNote } from './ScanProblemNote';
import type { ScanAgentActions, ScanAgentState } from './useScanAgent';

export interface ScanReadyProps {
  state: ScanAgentState;
  actions: ScanAgentActions;
  /** The pages are on their way into the job. */
  adding: boolean;
  /** Whether a job is open to take the pages (the sheet was opened from Add pages). */
  canAdd: boolean;
  onAdd: () => void;
}

/** Paige Scan is here: the scanner and its options, the pages so far, Scan or Stop, and the way into the job. */
export function ScanReady({ state, actions, adding, canAdd, onAdd }: ScanReadyProps): ReactNode {
  const scanning = state.phase === 'scanning';
  return (
    <>
      <ScanChoices state={state} actions={actions} busy={scanning || adding || state.configuring} />
      <ScanKeptOffer
        kept={state.kept}
        restoring={state.restoring}
        busy={scanning || adding}
        onAdd={(scan) => void actions.addKept(scan)}
        onDiscard={actions.discardKept}
      />
      <ScanPages pages={state.pages} scanning={scanning} onRemove={actions.removePage} />
      <ScanStatus state={state} />
      {state.problem && (
        <ScanProblemNote
          problem={state.problem}
          devices={state.devices}
          device={state.device}
          outcome={state.outcome}
          pages={state.scanned}
        />
      )}
      <ScanActions
        scanning={scanning}
        count={state.pages.length}
        busy={adding}
        canScan={Boolean(state.device) && !state.configuring}
        canAdd={canAdd}
        onScan={() => void actions.scan()}
        onStop={() => void actions.cancel()}
        stopping={state.stopping}
        onAdd={onAdd}
      />
    </>
  );
}

/** The scanner and its options, More for its own window, and the line that its saved settings are used. */
function ScanChoices(p: {
  state: ScanAgentState;
  actions: ScanAgentActions;
  busy: boolean;
}): ReactNode {
  const { state, actions, busy } = p;
  const more = (
    <ScanMore
      capabilities={state.capabilities}
      driverWindow={state.driverWindow}
      disabled={busy}
      onDriverWindow={actions.setDriverWindow}
      onSettings={() => void actions.openSettings()}
    />
  );
  return (
    <>
      <ScanControls
        devices={state.devices}
        device={state.device}
        capabilities={state.capabilities}
        options={state.options}
        disabled={busy}
        ownWindow={state.driverWindow}
        onDevice={actions.chooseDevice}
        onOptions={actions.setOptions}
        more={more}
      />
      <ScanSavedNote
        capabilities={state.capabilities}
        disabled={busy}
        onReset={() => void actions.resetSettings()}
      />
    </>
  );
}

/**
 * Where the scan is: the pages so far while the feeder runs, "finishing the sheet inside" after Stop
 * (those pages still come; nothing is half-lost), and after a scan that went well, the blank sides
 * left out.
 */
function ScanStatus({ state }: { state: ScanAgentState }): ReactNode {
  if (state.configuring) {
    return (
      <p className="paige-scan-status" role="status" aria-live="polite">
        <Loader2 size={15} className="paige-spin" aria-hidden="true" />
        Waiting for your scanner’s settings…
      </p>
    );
  }
  if (state.phase === 'scanning') {
    const starting = state.driverWindow
      ? 'Waiting for your scanner’s window…'
      : 'Starting the scanner…';
    const count =
      state.scanned === 0
        ? starting
        : `Scanning… ${state.scanned} page${state.scanned === 1 ? '' : 's'} so far`;
    return (
      <p className="paige-scan-status" role="status" aria-live="polite">
        <Loader2 size={15} className="paige-spin" aria-hidden="true" />
        {state.stopping ? 'Stopping: finishing the sheet already in the scanner…' : count}
      </p>
    );
  }
  return state.summary ? (
    <p className="paige-scan-status is-quiet" role="status">
      {state.summary}
    </p>
  ) : null;
}

interface ScanActionsProps {
  scanning: boolean;
  count: number;
  busy: boolean;
  canScan: boolean;
  canAdd: boolean;
  onScan: () => void;
  onStop: () => void;
  stopping: boolean;
  onAdd: () => void;
}

function ScanActions(p: ScanActionsProps): ReactNode {
  return (
    <div className="paige-scan-cta-row">
      {p.scanning ? <StopButton stopping={p.stopping} onStop={p.onStop} /> : <ScanButton {...p} />}
      {p.count > 0 && !p.scanning && (
        <button
          type="button"
          className="paige-scan-cta"
          disabled={p.busy || !p.canAdd}
          onClick={p.onAdd}
        >
          {p.busy ? 'Adding…' : `Add ${p.count} page${p.count === 1 ? '' : 's'} to the job`}
        </button>
      )}
    </div>
  );
}

/** Scan is the one big action until there are pages; then Add takes that place and Scan more steps back. */
function ScanButton(p: ScanActionsProps): ReactNode {
  return (
    <button
      type="button"
      className={p.count > 0 ? 'paige-scan-cta is-quiet' : 'paige-scan-cta'}
      disabled={p.busy || !p.canScan}
      onClick={p.onScan}
    >
      <ScanLine size={16} aria-hidden="true" />
      {p.count > 0 ? 'Scan more' : 'Scan'}
    </button>
  );
}

function StopButton({ stopping, onStop }: { stopping: boolean; onStop: () => void }): ReactNode {
  return (
    <button type="button" className="paige-scan-cta is-quiet" onClick={onStop} disabled={stopping}>
      <Square size={13} aria-hidden="true" />
      {stopping ? 'Stopping…' : 'Stop'}
    </button>
  );
}
